import { DropdownButton, DropdownCategoryTitle } from "../../../ui/Dropdown";
import { Icon } from "../../../ui/Icon";
import { Surface } from "../../../ui/Surface";
import { Toolbar } from "../../../ui/Toolbar";
import { useCallback } from "react";
import Box from "@mui/material/Box";
import { Popover } from "../../../ui/Popover";

const FONT_FAMILY_GROUPS = [
    {
        label: "Sans Serif",
        options: [
            { label: "Inter", value: "" },
            { label: "Arial", value: "Arial" },
            { label: "Helvetica", value: "Helvetica" },
        ],
    },
    {
        label: "Serif",
        options: [
            { label: "Times New Roman", value: "Times" },
            { label: "Garamond", value: "Garamond" },
            { label: "Georgia", value: "Georgia" },
        ],
    },
    {
        label: "Monospace",
        options: [
            { label: "Courier", value: "Courier" },
            { label: "Courier New", value: "Courier New" },
        ],
    },
];

const FONT_FAMILIES = FONT_FAMILY_GROUPS.flatMap(group => [group.options]).flat();

export type FontFamilyPickerProps = {
    onChange: (value: string) => void;
    value: string;
};

export const FontFamilyPicker = ({ onChange, value }: FontFamilyPickerProps) => {
    const currentValue = FONT_FAMILIES.find(size => size.value === value);
    const currentFontLabel = currentValue?.label.split(" ")[0] || "Inter";

    const selectFont = useCallback((font: string) => onChange(font), [onChange]);

    return (
        <Popover
            content={({ closePopup }) => (
                <Surface
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: "4px",
                        padding: "16px 8px",
                    }}>
                    {FONT_FAMILY_GROUPS.map(group => (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                marginTop: "10px",
                                "&:first-of-type": { marginTop: "0" },
                                width: "100%",
                                gap: "2px",
                            }}
                            key={group.label}>
                            <DropdownCategoryTitle>{group.label}</DropdownCategoryTitle>
                            {group.options.map(font => (
                                <DropdownButton
                                    isActive={value === font.value}
                                    onClick={() => {
                                        selectFont(font.value);
                                        closePopup();
                                    }}
                                    key={`${font.label}_${font.value}`}>
                                    <span
                                        style={{ fontFamily: font.value }}
                                        data-testid="auto-id-dca134ae-f1b6-42bc-b430-5fddd227241d">
                                        {font.label}
                                    </span>
                                </DropdownButton>
                            ))}
                        </Box>
                    ))}
                </Surface>
            )}>
            {({ openPopup }) => (
                <Toolbar.Button active={!!currentValue?.value} onClick={openPopup}>
                    {currentFontLabel}
                    <Icon name="ChevronDown" style={{ width: "8px", height: "8px" }} />
                </Toolbar.Button>
            )}
        </Popover>
    );
};
